import { Alert, Box, Card, CardContent, Typography } from '@mui/material'
import UploadIcon from '@mui/icons-material/Upload'
import { ChangeEvent, useCallback, useRef, useState } from 'react'
import { postChipData } from 'utils/api'
import { DeepcellPrimaryButton } from 'components/shared/DeepcellPrimaryButton'
import LinearProgressWithLabel from 'components/shared/LinearProgressWithLabel'

interface ChipExportFormat {
  exportDate?: string
  chipCount?: number
  chips: string[]
}

interface ChipError {
  chip: string
  error: string
  id: string
}

interface ImportResult {
  successful: number
  failed: number
  errors: ChipError[]
}

interface ImportProgress {
  current: number
  total: number
}

export const ChipImportTool = (): JSX.Element | null => {
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState<boolean>(false)
  const [progress, setProgress] = useState<ImportProgress | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const parseJsonFile = (file: File): Promise<ChipExportFormat> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = () => {
        try {
          const content = reader.result as string
          const parsed = JSON.parse(content) as ChipExportFormat

          // Validate structure
          if (!parsed.chips || !Array.isArray(parsed.chips)) {
            reject(new Error('Invalid JSON format: missing or invalid "chips" array'))
            return
          }

          // Validate each chip is a string (JWT)
          for (let i = 0; i < parsed.chips.length; i += 1) {
            if (typeof parsed.chips[i] !== 'string') {
              reject(new Error(`Invalid chip data at index ${i}: expected string JWT, got ${typeof parsed.chips[i]}`))
              return
            }
          }

          resolve(parsed)
        } catch (err) {
          reject(new Error(`Failed to parse JSON file: ${err instanceof Error ? err.message : 'Unknown error'}`))
        }
      }

      reader.onerror = () => {
        reject(new Error('Error reading file'))
      }

      reader.readAsText(file)
    })
  }

  const importChips = async (chipData: ChipExportFormat): Promise<void> => {
    const total = chipData.chips.length
    const errors: ChipError[] = []
    let successful = 0

    setProgress({ current: 0, total })

    // Process chips sequentially to avoid overwhelming the API
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < chipData.chips.length; i++) {
      const chipJwt = chipData.chips[i]

      try {
        // eslint-disable-next-line no-await-in-loop
        await postChipData(chipJwt)
        successful += 1
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        errors.push({
          chip: chipJwt.substring(0, 50),
          error: errorMessage,
          id: `error-${i}`,
        })
      }

      setProgress({ current: i + 1, total })
    }

    setResult({
      successful,
      failed: errors.length,
      errors,
    })
  }

  const handleFileUpload = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    // Reset state
    setError(null)
    setResult(null)
    setProgress(null)

    if (!e.target.files || e.target.files.length === 0) {
      return
    }

    const file = e.target.files[0]

    // Reset input so the same file can be uploaded again
    if (uploadInputRef.current) {
      uploadInputRef.current.value = ''
    }

    // Validate file extension
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (extension !== 'json') {
      setError(`Invalid file type: .${extension}. Please upload a JSON file.`)
      return
    }

    setImporting(true)

    try {
      const chipData = await parseJsonFile(file)

      if (chipData.chips.length === 0) {
        setError('No chips found in the uploaded file')
        setImporting(false)
        return
      }

      await importChips(chipData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import chips')
    } finally {
      setImporting(false)
    }
  }, [])

  return (
    <Card sx={{ maxWidth: 800, margin: '0 auto' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Chip Import Tool
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload a JSON file containing chip data to import chips into this on-premises environment.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <DeepcellPrimaryButton
            contained
            startIcon={<UploadIcon />}
            onClick={() => uploadInputRef.current?.click()}
            disabled={importing}
          >
            Upload JSON File
            <input
              ref={uploadInputRef}
              data-testid="chip-import-input"
              hidden
              type="file"
              accept=".json"
              onChange={handleFileUpload}
            />
          </DeepcellPrimaryButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {progress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Importing {progress.current}/{progress.total} chips...
            </Typography>
            <LinearProgressWithLabel
              value={(progress.current / progress.total) * 100}
            />
          </Box>
        )}

        {result && (
          <Box>
            <Alert severity={result.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              Import complete: {result.successful} successful, {result.failed} failed
            </Alert>

            {result.errors.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Error Details:
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    border: '1px solid #dedede',
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  {result.errors.map((err) => (
                    <Typography
                      key={err.id}
                      variant="body2"
                      sx={{ fontFamily: 'monospace', mb: 1 }}
                    >
                      {err.chip}: {err.error}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default ChipImportTool
